import React from "react";
import {
  Box,
  Text,
  Avatar,
  createStyles,
  Timeline,
  ScrollArea,
  Flex,
  Center,
} from "@mantine/core";
import { IconClock } from "@tabler/icons-react";
import { useGetTimeUtilization } from "api/attendance/gettodayTimeUtilization";
import { formatToLocalDateTime, formatToLocalTime } from "utils/dateFormate";

interface RawActivity {
  id: string;
  name: string;
  type: string;
  time: string;
  duration: number;
}

type ActivityType = "Punch In" | "Punch Out" | "Break";
interface Activity {
  id: string;
  type: ActivityType;
  time: string;
  duration?: string;
}

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    boxShadow: theme.shadows.sm,
    padding: theme.spacing.md,
    width: "100%",
    minHeight: "30vh",
    height: "47vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    marginBottom: theme.spacing.sm,
    fontSize: "16px",
    flex: "0 0 auto",
  },
  scroll: {
    flex: "1 1 auto",
  },
  timeline: {
    paddingLeft: 0,
    [`& .mantine-Timeline-line`]: {
      borderStyle: "dashed",
      borderWidth: 2,
      borderColor: theme.colors.gray[3],
    },
    [`& .mantine-Timeline-item`]: {
      padding: 0,
    },
    [`& .mantine-Timeline-itemBody`]: {
      marginLeft: theme.spacing.lg,
    },
  },
  itemWrapper: {
    padding: `${theme.spacing.sm}px ${theme.spacing.xs}px`,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    "&:last-of-type": {
      borderBottom: "none",
    },
  },
  avatar: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 700,
  },
  activityText: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
  },
  timeLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.gray[6],
  },
  clockIcon: {
    width: 14,
    height: 14,
  },
  emptyState: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: theme.colors.gray[6],
    height: "100%",
  },
  emptyText: {
    fontSize: theme.fontSizes.sm,
    marginTop: theme.spacing.md,
  },
}));

const activityConfig: Record<ActivityType, { initials: string; bg: string }> = {
  "Punch In": { initials: "CI", bg: "#69DB7C" },
  "Punch Out": { initials: "CO", bg: "#D27D7D" },
  Break: { initials: "BR", bg: "#F2C94C" },
};

interface RecentActivityProps {
  userId?: string | null;
}

export function RecentActivity({ userId }: RecentActivityProps) {
  const { classes, theme } = useStyles();
  const { data, isLoading } = useGetTimeUtilization(userId || undefined);

  if (isLoading) {
    return (
      <Box className={classes.card}>
        <Text weight={500} className={classes.header}>
          Recent Activity
        </Text>
        <Text>Loading...</Text>
      </Box>
    );
  }

  // Check if data exists and has activities
  const hasData = data && data.data && data.data.activities;
  const raw = hasData ? (data.data.activities as RawActivity[]) : [];

  if (!hasData || raw.length === 0) {
    return (
      <Box className={classes.card}>
        <Text weight={500} className={classes.header}>
          Recent Activity
        </Text>
        <Center className={classes.emptyState}>
          <IconClock size={40} color={theme.colors.gray[4]} />
          <Text className={classes.emptyText}>No activities found</Text>
        </Center>
      </Box>
    );
  }

  const activities: Activity[] = raw.map((item) => {
    const dur = item.duration > 0 ? `${item.duration} Hr` : undefined;
    const atype =
      (item.type as ActivityType) in activityConfig
        ? (item.type as ActivityType)
        : "Punch In";

    return {
      id: item.id,
      type: atype,
      time: item.time,
      duration: dur,
    };
  });

  const defaultConfig = { initials: "--", bg: theme.colors.gray[4] };

  return (
    <Box className={classes.card}>
      <Text weight={500} className={classes.header}>
        Recent Activity
      </Text>

      <ScrollArea className={classes.scroll}>
        <Timeline
          active={activities.length}
          bulletSize={30}
          className={classes.timeline}
        >
          {activities.map(({ id, type, time }) => {
            const config = activityConfig[type] ?? defaultConfig;
            const label = `${type} at`;
            return (
              <Timeline.Item
                key={id}
                bullet={
                  <Avatar
                    radius="xl"
                    size={30}
                    className={classes.avatar}
                    style={{ backgroundColor: config.bg, color: "#fff" }}
                  >
                    {config.initials}
                  </Avatar>
                }
                className={classes.itemWrapper}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "90%" }}
                >
                  <Text className={classes.activityText}>{label}</Text>
                  <Text
                    className={classes.timeLabel}
                    title={formatToLocalDateTime(time)}
                  >
                    <IconClock className={classes.clockIcon} />
                    {formatToLocalTime(time)}
                  </Text>
                </Flex>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </ScrollArea>
    </Box>
  );
}
